import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { MeshDevicePanel } from '../components/meshcentral/MeshDevicePanel';
import { Button, buttonVariants } from '../components/ui/button';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { meshNodeIdFromPath } from '../lib/meshcentral';
import { cn } from '../lib/utils';
import { MeshDeviceDto } from '../models';
import { getApiError, getMeshCentralDevices } from '../services/api';

export const MeshDevicePage = () => {
  const { nodeId: nodeIdParam } = useParams();
  const nodeId = meshNodeIdFromPath(nodeIdParam);
  const location = useLocation();
  const navigationDevice = (location.state as { device?: MeshDeviceDto } | null)?.device;
  const initialDevice = navigationDevice?.nodeId === nodeId ? navigationDevice : undefined;
  const [device, setDevice] = useState<MeshDeviceDto | undefined>(initialDevice);
  const [loaded, setLoaded] = useState(Boolean(initialDevice));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setError('');
    try {
      const response = await getMeshCentralDevices();
      setDevice(response.data.data.find((item) => item.nodeId === nodeId));
    } catch (e) {
      if (!silent) setError(getApiError(e));
    } finally {
      setLoaded(true);
    }
  }, [nodeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => load(true), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return <AppLayout title={device?.name || 'Устройство MeshCentral'}>
    <Link to="/meshcentral" className="mb-4 inline-flex cursor-pointer items-center gap-2 rounded-lg text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <ArrowLeft className="h-4 w-4" />К парку устройств
    </Link>

    <PageHeader
      title={device?.name || 'Устройство MeshCentral'}
      description={device ? `${device.hostname || 'Без hostname'} · ${device.groupName || 'Без группы'} · операции напрямую по Node ID` : 'Полная карточка MeshCentral-устройства.'}
      action={<Button variant="outline" onClick={refresh} disabled={refreshing}><RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />Обновить</Button>}
    />

    {error && <div role="alert" className="mb-5 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
    {!loaded ? <LoadingState /> : !device ? <section className="rounded-2xl border border-dashed border-border bg-card p-10 text-center shadow-sm"><h2 className="text-lg font-semibold">Устройство не найдено</h2><p className="mt-2 text-sm text-muted-foreground">Node ID отсутствует в текущем инвентаре MeshCentral.</p><Link to="/meshcentral" className={cn(buttonVariants(), 'mt-5')}>Вернуться к списку</Link></section> : <MeshDevicePanel device={device} variant="page" />}
  </AppLayout>;
};
